//frontend/src/components/checkout/GuestCheckoutPage.js - Enhanced with pincode persistence

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { saveShippingInfo } from '../../slices/cartSlice';
import {
    User,
    Mail,
    Phone,
    MapPin,
    ChevronRight,
    AlertTriangle,
    Truck,
    RefreshCw,
    CheckCircle,
    Edit2
} from 'lucide-react';
import MetaData from '../layouts/MetaData';

const GuestCheckoutPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { items: cartItems } = useSelector(state => state.cartState);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        postalCode: '',
        city: 'Bengaluru',
        state: 'Karnataka',
        country: 'India'
    });
    
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [pincodeLoading, setPincodeLoading] = useState(false);
    const [deliveryInfo, setDeliveryInfo] = useState(null);
    const [pincodeError, setPincodeError] = useState('');
    const [manualPincode, setManualPincode] = useState('');
    const [showManualInput, setShowManualInput] = useState(false);

    // Available delivery areas based on your requirements
    const deliveryAreas = [
        { code: '560098', area: 'Rajarajeshwari Nagar', hubPlace: 'Kengeri' },
        { code: '560060', area: 'Kengeri', hubPlace: 'Kengeri' },
        { code: '560060', area: 'Viswaneedam', hubPlace: 'Segehalli' },
        { code: '560056', area: 'Bangalore Viswavidalaya', hubPlace: 'Kengeri' },
        { code: '562130', area: 'Seegehalli', hubPlace: 'Segehalli' }
    ];

    // Load saved data on component mount
    useEffect(() => {
        // Check if user came from cart as guest
        const isGuestCheckout = localStorage.getItem('isGuestCheckout');
        if (!isGuestCheckout) {
            navigate('/cart');
            return;
        }

        // Check for cart items
        if (!cartItems.length) {
            navigate('/cart');
            return;
        }

        // Load saved form data
        const savedFormData = localStorage.getItem('guestFormData');
        if (savedFormData) {
            try {
                const parsedData = JSON.parse(savedFormData);
                setFormData(prev => ({ ...prev, ...parsedData }));
                
                // If we have saved step, restore it
                const savedStep = localStorage.getItem('guestCheckoutStep');
                if (savedStep) {
                    setStep(parseInt(savedStep));
                }
            } catch (error) {
                console.error('Error parsing saved form data:', error);
            }
        }

        // Load saved pincode data from cart page
        const savedDeliveryInfo = localStorage.getItem('selectedDeliveryInfo');
        const savedPincode = localStorage.getItem('selectedPincode');
        
        if (savedDeliveryInfo && savedPincode) {
            try {
                const parsedInfo = JSON.parse(savedDeliveryInfo);
                setDeliveryInfo(parsedInfo);
                setFormData(prev => ({ 
                    ...prev, 
                    postalCode: savedPincode 
                }));
                console.log('Loaded saved delivery info:', parsedInfo);
            } catch (error) {
                console.error('Error parsing saved delivery info:', error);
            }
        }
    }, [cartItems, navigate]);

    // Save form data whenever it changes
    useEffect(() => {
        localStorage.setItem('guestFormData', JSON.stringify(formData));
        localStorage.setItem('guestCheckoutStep', step.toString());
    }, [formData, step]);

    // Check pincode delivery charges
    const checkPincode = async (pincode) => {
        if (!pincode || pincode.length !== 6) {
            setPincodeError('Please enter a valid 6-digit pincode');
            return;
        }

        try {
            setPincodeLoading(true);
            setPincodeError('');

            const { data } = await axios.post('/api/v1/pincode/check', {
                pincode: pincode
            });

            if (data.success && data.isServiceable) {
                setDeliveryInfo(data.data);
                setFormData(prev => ({ 
                    ...prev, 
                    postalCode: pincode 
                }));

                // Save to localStorage for persistence
                localStorage.setItem('selectedDeliveryInfo', JSON.stringify(data.data));
                localStorage.setItem('selectedPincode', pincode);

                toast.success(data.message);
                setShowManualInput(false);
            } else {
                setPincodeError(data.message);
                setDeliveryInfo(null);
            }
        } catch (error) {
            console.error('Pincode check error:', error);
            const errorMessage = error.response?.data?.message || 'Error checking pincode';
            setPincodeError(errorMessage);
            setDeliveryInfo(null);
        } finally {
            setPincodeLoading(false);
        }
    };

    // Handle dropdown selection
    const handleAreaSelect = async (selectedPincode) => {
        if (selectedPincode) {
            await checkPincode(selectedPincode);
        } else {
            setDeliveryInfo(null);
            setFormData(prev => ({ ...prev, postalCode: '' }));
            localStorage.removeItem('selectedDeliveryInfo');
            localStorage.removeItem('selectedPincode');
        }
    };

    // Handle manual pincode input
    const handleManualPincodeCheck = async () => {
        await checkPincode(manualPincode);
        setManualPincode('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (step === 1) {
            // Validate phone number
            if (!formData.phone || formData.phone.length !== 10) {
                toast.error('Please enter a valid phone number');
                return;
            }
            setStep(2);
            return;
        }

        // Step 2 validation
        if (!formData.name.trim()) {
            toast.error('Please enter your full name');
            return;
        }

        if (!formData.address.trim()) {
            toast.error('Please enter your delivery address');
            return;
        }

        if (!deliveryInfo || !formData.postalCode) {
            toast.error('Please select a delivery area and check delivery charges');
            return;
        }

        try {
            setLoading(true);
            
            // Save guest info
            localStorage.setItem('guestInfo', JSON.stringify(formData));

            // Save shipping info with delivery details
            dispatch(saveShippingInfo({
                address: formData.address,
                city: 'Bengaluru',
                phoneNo: formData.phone,
                postalCode: formData.postalCode,
                country: 'India',
                state: 'Karnataka',
                areaName: deliveryInfo.area,
                hubPlace: deliveryInfo.hubPlace
            }));

            // Clear temporary storage
            localStorage.removeItem('guestFormData');
            localStorage.removeItem('guestCheckoutStep');

            navigate('/order/confirm');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error processing checkout');
        } finally {
            setLoading(false);
        }
    };

    const renderStep1 = () => (
        <div className="space-y-6">
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Phone Number*
                </label>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="tel"
                        maxLength="10"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            phone: e.target.value.replace(/\D/g, '')
                        }))}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-red-600 focus:border-red-600 text-center text-sm"
                        placeholder="Enter 10-digit mobile number"
                        required
                    />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                    {formData.phone.length}/10 digits
                </p>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Email <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            email: e.target.value
                        }))}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-red-600 focus:border-red-600 text-center text-sm"
                        placeholder="Enter your email address"
                    />
                </div>
            </div>

            <button
                type="button"
                onClick={handleSubmit}
                disabled={!formData.phone || formData.phone.length !== 10}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
                Continue
                <ChevronRight className="w-5 h-5" />
            </button>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6">
            

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Full Name*
                </label>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            name: e.target.value
                        }))}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-red-600 focus:border-red-600 text-center text-sm"
                        placeholder="Enter your full name"
                        required
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Delivery Address*
                </label>
                <div className="relative">
                    <div className="absolute top-3 left-3">
                        <MapPin className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                        value={formData.address}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            address: e.target.value
                        }))}
                        rows="3"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-red-600 focus:border-red-600 text-center text-sm"
                        placeholder="Enter your complete address"
                        required
                    />
                </div>
            </div>

            {/* Enhanced Delivery Area Selection */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Select Delivery Area*
                </label>
                
                {/* Dropdown Selection */}
                <div className="space-y-3">
                    <select
                        value={formData.postalCode}
                        onChange={(e) => handleAreaSelect(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-red-600 focus:border-red-600 text-center text-sm"
                    >
                        <option value="">Select delivery area</option>
                        {deliveryAreas.map((area, index) => (
                            <option key={`${area.code}-${index}`} value={area.code}>
                                {area.area} - {area.code} (Hub: {area.hubPlace})
                            </option>
                        ))}
                    </select>

                    {/* Manual Pincode Input Option */}
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={() => setShowManualInput(!showManualInput)}
                            className="text-sm text-red-600 hover:text-red-700 flex items-center justify-center gap-1 mx-auto"
                        >
                            <Edit2 className="w-4 h-4" />
                            {showManualInput ? 'Use dropdown instead' : 'Or enter pincode manually'}
                        </button>
                    </div>

                    {/* Manual Pincode Input */}
                    {showManualInput && (
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Enter 6-digit Pincode
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    maxLength="6"
                                    value={manualPincode}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        setManualPincode(value);
                                        setPincodeError('');
                                    }}
                                    className="flex-grow px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-red-600 focus:border-red-600 text-center"
                                    placeholder="Enter pincode"
                                />
                                <button
                                    type="button"
                                    onClick={handleManualPincodeCheck}
                                    disabled={pincodeLoading || manualPincode.length !== 6}
                                    className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                >
                                    {pincodeLoading ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        'Check'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Pincode Error */}
                {pincodeError && (
                    <div className="flex items-start gap-2 text-red-600 text-sm mt-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <p>{pincodeError}</p>
                    </div>
                )}

                {/* Delivery Info Display */}
                {deliveryInfo && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-3">
                        <div className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                            <div>
                                <p className="font-medium text-sm text-green-800">
                                    ✅ Delivery Available for {deliveryInfo.area}
                                </p>
                                <p className="text-xs text-green-600 mt-1">
                                    📍 Hub: {deliveryInfo.hubPlace} | 🚚 Delivery Charge: ₹{deliveryInfo.deliveryCharge}
                                </p>
                                <p className="text-xs text-green-600">
                                    Pincode: {formData.postalCode}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Back and Continue Buttons */}
            <div className="flex gap-3">
                <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                    Back
                </button>
                <button
                    type="submit"
                    disabled={loading || !formData.name || !formData.address || !deliveryInfo}
                    className="flex-2 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                    {loading ? (
                        <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Processing...
                        </>
                    ) : (
                        <>
                            <Truck className="w-4 h-4" />
                            Proceed to Checkout
                        </>
                    )}
                </button>
            </div>

            
        </div>
    );

    return (
        <div
            style={{
                backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                backgroundRepeat: 'repeat',
                backgroundSize: 'auto',
                backgroundPosition: 'center'
            }}
            className="min-h-screen py-8"
        >
            <MetaData title="Guest Checkout" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-md mx-auto">
                    <div className="text-center mb-6">
                        <h1 className="text-2xl md:text-3xl font-bold text-red-600 mb-3">
                            Guest Checkout
                        </h1>
                        <p className="text-base text-gray-600">
                            {step === 1 ? 'Please provide your contact information' : 'Enter your delivery details'}
                        </p>
                        
                        {/* Step Indicator */}
                        <div className="flex items-center justify-center mt-4 space-x-4">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                step >= 1 ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                                1
                            </div>
                            <div className={`w-8 h-1 ${step >= 2 ? 'bg-red-600' : 'bg-gray-200'}`}></div>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                step >= 2 ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                                2
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <form onSubmit={handleSubmit}>
                            {step === 1 ? renderStep1() : renderStep2()}
                        </form>
                    </div>

                    {/* Saved Data Indicator */}
                    {(formData.name || formData.phone || deliveryInfo) && (
                        <div className="mt-4 text-center text-xs text-gray-500">
                            💾 Your information is automatically saved
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GuestCheckoutPage;